export const CREW_JOBS_ANALYTICS_EVENTS = [
  'page_view',
  'job_card_click',
  'job_view',
  'commit_cta_click',
  'commit_submit',
] as const;

export type CrewJobsAnalyticsEventName = (typeof CREW_JOBS_ANALYTICS_EVENTS)[number];

export type CrewJobsAnalyticsAttribution = {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  ref: string | null;
  from: string | null;
};

export type CrewJobsAnalyticsRawRow = {
  event: string;
  path?: string | null;
  jobId?: string | null;
  jobSlug?: string | null;
  jobTitle?: string | null;
  clientId?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  ref?: string | null;
  from?: string | null;
  createdAtMs?: number;
};

export type CrewJobsJobEngagementRow = {
  key: string;
  jobId: string | null;
  jobSlug: string | null;
  jobTitle: string | null;
  views: number;
  cardClicks: number;
  commitClicks: number;
  commitSubmits: number;
  score: number;
};

export type CrewJobsAnalyticsSummary = {
  days: number;
  pageViews: number;
  uniqueVisitors: number;
  jobViews: number;
  cardClicks: number;
  commitClicks: number;
  commitSubmits: number;
  topJobs: CrewJobsJobEngagementRow[];
  attribution: Array<{ label: string; count: number }>;
};

export function attributionFromSearchParams(params: URLSearchParams): CrewJobsAnalyticsAttribution {
  const clip = (v: string | null, max: number) => {
    const s = (v || '').trim();
    return s ? s.slice(0, max) : null;
  };
  return {
    utmSource: clip(params.get('utm_source'), 80),
    utmMedium: clip(params.get('utm_medium'), 80),
    utmCampaign: clip(params.get('utm_campaign'), 120),
    ref: clip(params.get('ref'), 80),
    from: clip(params.get('from'), 80),
  };
}

function attributionLabel(row: CrewJobsAnalyticsRawRow): string {
  const campaign = row.utmCampaign?.trim();
  const source = row.utmSource?.trim();
  const medium = row.utmMedium?.trim();
  const ref = row.ref?.trim() || row.from?.trim();
  if (source || medium || campaign) {
    return [source || 'unknown', medium || 'unknown', campaign || '—'].join(' / ');
  }
  if (ref) return `ref:${ref}`;
  return '(direct / none)';
}

function jobKey(row: CrewJobsAnalyticsRawRow): string | null {
  if (row.jobId) return `id:${row.jobId}`;
  if (row.jobSlug) return `slug:${row.jobSlug}`;
  return null;
}

export function summarizeCrewJobsAnalytics(
  rows: CrewJobsAnalyticsRawRow[],
  days: number,
): CrewJobsAnalyticsSummary {
  const visitors = new Set<string>();
  let pageViews = 0;
  let jobViews = 0;
  let cardClicks = 0;
  let commitClicks = 0;
  let commitSubmits = 0;
  const jobs = new Map<string, CrewJobsJobEngagementRow>();
  const attrCounts = new Map<string, number>();

  const bumpJob = (
    row: CrewJobsAnalyticsRawRow,
    field: 'views' | 'cardClicks' | 'commitClicks' | 'commitSubmits',
  ) => {
    const key = jobKey(row);
    if (!key) return;
    let cur = jobs.get(key);
    if (!cur) {
      cur = {
        key,
        jobId: row.jobId || null,
        jobSlug: row.jobSlug || null,
        jobTitle: row.jobTitle || null,
        views: 0,
        cardClicks: 0,
        commitClicks: 0,
        commitSubmits: 0,
        score: 0,
      };
      jobs.set(key, cur);
    }
    if (row.jobTitle && !cur.jobTitle) cur.jobTitle = row.jobTitle;
    if (row.jobId && !cur.jobId) cur.jobId = row.jobId;
    if (row.jobSlug && !cur.jobSlug) cur.jobSlug = row.jobSlug;
    cur[field] += 1;
  };

  for (const row of rows) {
    if (row.clientId) visitors.add(row.clientId);
    const label = attributionLabel(row);
    attrCounts.set(label, (attrCounts.get(label) || 0) + 1);

    switch (row.event) {
      case 'page_view':
        pageViews += 1;
        break;
      case 'job_view':
        jobViews += 1;
        bumpJob(row, 'views');
        break;
      case 'job_card_click':
        cardClicks += 1;
        bumpJob(row, 'cardClicks');
        break;
      case 'commit_cta_click':
        commitClicks += 1;
        bumpJob(row, 'commitClicks');
        break;
      case 'commit_submit':
        commitSubmits += 1;
        bumpJob(row, 'commitSubmits');
        break;
      default:
        break;
    }
  }

  const topJobs = Array.from(jobs.values())
    .map(j => ({
      ...j,
      score: j.views + j.cardClicks * 2 + j.commitClicks * 3 + j.commitSubmits * 5,
    }))
    .sort((a, b) => b.score - a.score || b.views - a.views)
    .slice(0, 15);

  const attribution = Array.from(attrCounts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);

  return {
    days,
    pageViews,
    uniqueVisitors: visitors.size,
    jobViews,
    cardClicks,
    commitClicks,
    commitSubmits,
    topJobs,
    attribution,
  };
}
